// Client-side metrics engine for the Typing Test question type.
// Parallel to tryCodingRunner.js — pure logic used by TypingTestPlayer.js.

const CHARS_PER_WORD = 5;

const round1 = (n) => Math.round(n * 10) / 10;

export const createKeystrokeLog = () => [];

export const logKeystroke = (log, char, timestamp = Date.now()) => {
  log.push({ char, timestamp });
  return log;
};

// Diffs `typed` against `passage` character-by-character and returns live metrics.
export const computeMetrics = ({ passage, typed, startedAt, now = Date.now() }) => {
  const minutesElapsed = Math.max((now - startedAt) / 60000, 1 / 60000);
  const totalCharsTyped = typed.length;

  let correctChars = 0;
  let incorrectChars = 0;
  for (let i = 0; i < totalCharsTyped; i++) {
    if (typed[i] === passage[i]) {
      correctChars++;
    } else {
      incorrectChars++;
    }
  }

  const grossWPM = (totalCharsTyped / CHARS_PER_WORD) / minutesElapsed;
  const netWPM = Math.max(0, ((totalCharsTyped - incorrectChars) / CHARS_PER_WORD) / minutesElapsed);
  const accuracyPct = totalCharsTyped > 0 ? (correctChars / totalCharsTyped) * 100 : 100;

  return {
    grossWPM: round1(grossWPM),
    netWPM: round1(netWPM),
    accuracyPct: round1(accuracyPct),
    correctChars,
    incorrectChars,
    errorCount: incorrectChars,
    totalCharsTyped,
  };
};

// Builds the final payload sent to the backend on submit: raw keystroke log + summary metrics.
// `cumulativeErrorCount`, when passed, overrides metrics.incorrectChars/errorCount — those are
// otherwise diff-based and only reflect characters currently present in `typed`, so a corrected
// (backspaced) mistake silently disappears from them. cumulativeErrorCount is a running total
// tracked by the caller (TypingTestPlayer) that only ever grows, so mistakes stay counted even
// after correction.
export const buildSubmitPayload = ({ passage, typed, keystrokeLog, startedAt, submittedAt, backspaceCount, cumulativeErrorCount }) => {
  const metrics = computeMetrics({ passage, typed, startedAt, now: submittedAt });

  // --- Keystroke-based totals -------------------------------------------------
  // Every forward character the candidate ever typed is one keystroke — that is exactly what
  // keystrokeLog records (Backspace is not a character, so it is never logged). If the same
  // passage position is fumbled several times (type wrong → backspace → type wrong again →
  // backspace → type right) every one of those attempts is its own keystroke, and every wrong
  // attempt is counted in cumulativeErrorCount (a running total the caller only ever increments).
  // So corrections are never "free": each mistake and each retype still counts.
  //   totalCharsTyped = all attempts            (e.g. 103 for a 100-char passage typed with 3 fumbles)
  //   incorrectChars  = every wrong attempt     (cumulativeErrorCount)
  //   correctChars    = totalCharsTyped - incorrectChars
  //   accuracyPct     = correctChars / totalCharsTyped * 100
  // Falls back to the diff-based metrics if the keystroke log / cumulative count isn't available
  // (e.g. an old client or a direct computeMetrics caller). With zero corrections the two models
  // agree exactly (keystrokeLog.length === typed.length, cumulativeErrorCount === incorrectChars).
  const haveKeystrokeData = Array.isArray(keystrokeLog) && keystrokeLog.length > 0 && cumulativeErrorCount != null;
  const totalCharsTyped = haveKeystrokeData ? keystrokeLog.length : metrics.totalCharsTyped;
  const incorrectChars = haveKeystrokeData ? cumulativeErrorCount : metrics.incorrectChars;
  const correctChars = Math.max(0, totalCharsTyped - incorrectChars);
  const accuracyPct = totalCharsTyped > 0 ? round1((correctChars / totalCharsTyped) * 100) : 100;

  // Backspace rate is measured against the same keystroke total, not the final passage length,
  // so heavy fumbling on a few characters still shows up. Not rounded here — round1 would zero
  // out the common sub-5% case; callers round for display (rate * 100).
  const backspaceRate = totalCharsTyped > 0 ? backspaceCount / totalCharsTyped : 0;

  return {
    keystrokeLog,
    startedAt,
    submittedAt,
    timeTakenSec: Math.max(0, Math.round((submittedAt - startedAt) / 1000)),
    backspaceCount,
    backspaceRate,
    ...metrics,
    // Keystroke-based overrides (win over the diff-based values spread from `metrics` above).
    totalCharsTyped,
    correctChars,
    incorrectChars,
    errorCount: incorrectChars,
    accuracyPct,
    // Final-state view kept alongside: how much of the delivered passage is right, ignoring history.
    finalCharsTyped: metrics.totalCharsTyped,
    finalCorrectChars: metrics.correctChars,
  };
};
